<template>
    <div class="w-100 align-self-start">
        <v-skeleton-loader v-if="fetchingAllData" type="article, table"></v-skeleton-loader>
        <v-card flat color="transparent" v-else>
            <v-card-title v-if="!hideHeader" class="d-block">
                <v-btn
                    class="mb-3"
                    small
                    text
                    color="secondary"
                    :to="{
                        name: 'snapshot-applications',
                        params: { oid: $route.params.oid, sid: $route.params.sid, iid: $route.params.iid, snid: $route.params.snid }
                    }">
                    <v-icon left>mdi-arrow-left</v-icon>
                    Applications
                </v-btn>
                <div>Application resources</div>
            </v-card-title>
            <v-card-subtitle v-if="!hideHeader">{{ selectedApp.long_id }}</v-card-subtitle>

            <v-card-text :class="{ 'px-0': selectionOnly }">
                <v-card class="mt-5 overflow-hidden d-flex">
                    <v-tabs v-model="tab">
                        <v-tab>standard</v-tab>
                        <v-tab v-if="cloudTabs.includes('azure')">elastic</v-tab>
                        <v-divider v-if="cloudTabs.includes('azure') && remainingCloudTabs.length" vertical class="mx-3"></v-divider>
                        <v-tab v-for="cloud in remainingCloudTabs" :key="`cloud-tab-${cloud}`">
                            {{ cloudTabLabel(cloud) }}
                        </v-tab>
                    </v-tabs>
                    <v-btn
                        v-if="activeCloudName && activeCloudName !== 'switch'"
                        text
                        tile
                        class="py-6"
                        :to="mappedResourcePoolId ? { name: 'reporting-dashboard', query: { rpid: mappedResourcePoolId } } : ''">
                        <div class="d-flex align-center">
                            <span class="mr-1 secondary--text">Current balance:</span>
                            <v-chip color="error lighten-4 error--text" small v-if="$isError(resourcePoolBalance) || resourcePoolBalance === null">
                                <v-icon small color="warning" left>mdi-alert-rhombus-outline</v-icon>
                                error
                            </v-chip>
                            {{ toLocaleFixed(resourcePoolBalance) }}
                            <v-icon right small v-if="mappedResourcePoolId">mdi-open-in-new</v-icon>
                        </div>
                    </v-btn>
                </v-card>

                <v-tabs-items v-model="tab" class="mt-3">
                    <v-tab-item>
                        <v-card flat color="transparent">
                            <v-card-text>
                                <v-alert text type="info" class="mb-6">{{ NCUHint }}</v-alert>
                                <div class="d-flex align-center">
                                    <v-spacer></v-spacer>
                                    <v-text-field
                                        prepend-inner-icon="mdi-magnify "
                                        autofocus
                                        hide-details
                                        solo
                                        flat
                                        dense
                                        placeholder="Search for a resource..."
                                        v-model="search.standard"
                                        clearable
                                        class="mr-2"></v-text-field>
                                </div>
                                <v-row no-gutters class="py-4">
                                    <v-col cols="2">
                                        <v-btn :ripple="false" plain block class="justify-start text-notransform" @click="headerClicked('cpu', 'standard')">
                                            <span class="subtitle-2 font-weight-medium text--secondary">vCPU</span>
                                            <v-icon small :class="headerClasses.standard.cpu">mdi-arrow-up</v-icon>
                                        </v-btn>
                                    </v-col>
                                    <v-col cols="3">
                                        <v-btn :ripple="false" plain block class="justify-start text-notransform" @click="headerClicked('memory', 'standard')">
                                            <span class="subtitle-2 font-weight-medium text--secondary">Memory</span>
                                            <v-icon small :class="headerClasses.standard.memory">mdi-arrow-up</v-icon>
                                        </v-btn>
                                    </v-col>
                                    <v-col cols="7" class="d-flex align-center justify-end">
                                        <span class="px-4 subtitle-2 font-weight-medium text--secondary">Actions</span>
                                    </v-col>
                                </v-row>
                                <v-data-iterator
                                    :items="filteredNCUOptions"
                                    no-data-text="No resources found."
                                    :search="search.standard"
                                    :sort-by="sortBy.standard"
                                    :sort-desc="sortDesc.standard"
                                    :options="tableOptions"
                                    :footer-props="{ 'items-per-page-options': itemsPerPageOptions }">
                                    <template v-slot:default="{ items }">
                                        <v-row>
                                            <v-col v-for="item in items" :key="item.id" cols="12">
                                                <v-card elevation="4" :class="getStandardItemClass(item)" :disabled="scalingApp">
                                                    <v-row no-gutters>
                                                        <v-col cols="2" class="d-flex align-center px-4">{{ item.cpu }}</v-col>
                                                        <v-col cols="3" class="d-flex align-center px-4">{{ toLocaleFixed(item.memory, 0) }} GB</v-col>
                                                        <v-col cols="7" class="text-right pa-4">
                                                            <template
                                                                v-if="item.id === selectedRow.standard && selectedApp.active_resource === 'ncu' && !scalingApp">
                                                                <v-btn color="success" small>
                                                                    <v-icon small>check</v-icon>
                                                                </v-btn>
                                                            </template>
                                                            <template v-else>
                                                                <v-btn
                                                                    color="primary"
                                                                    small
                                                                    :disabled="appScaled"
                                                                    @click="clickRow(item.id, 'standard')"
                                                                    :loading="
                                                                        item.id === selectedRow.standard && selectedApp.active_resource === 'ncu' && scalingApp
                                                                    ">
                                                                    {{ selectionOnly ? 'select' : 'set' }}
                                                                </v-btn>
                                                            </template>
                                                        </v-col>
                                                    </v-row>
                                                </v-card>
                                            </v-col>
                                        </v-row>
                                    </template>
                                </v-data-iterator>
                            </v-card-text>
                        </v-card>
                    </v-tab-item>
                    <v-tab-item v-for="cloud in cloudTabs" :key="`cloud-tab-item-${cloud}`">
                        <v-card flat color="transparent">
                            <v-card-text>
                                <v-alert type="info" text v-if="cloud === 'azure' && !canScaleGlobal" class="mb-6">
                                    <v-row align="center">
                                        <v-col class="grow">
                                            <div v-if="hpcDisabledReason === 'TURNED_OFF_RESOURCE_POOL'">
                                                Contact your resource pool manager to enable this feature.
                                            </div>
                                            <div v-if="hpcDisabledReason === 'TURNED_OFF_ORG'">Contact your Organization's manager to enable this feature.</div>
                                            <div v-if="hpcDisabledReason === 'ARCHIVED'">
                                                Pay-as-you-go resources are unavailable because the space is archived.
                                            </div>
                                            <div v-if="hpcDisabledReason === 'DISABLED'">Contact Nuvolos support to enable this feature.</div>
                                            <template v-if="!hpcDisabledReason">
                                                <div v-if="!isMasterInstance && currentSpaceType === spaceTypes.EDUCATION_SPACE">
                                                    Pay-as-you-go resources are only available in the Master instance in teaching spaces.
                                                </div>
                                                <div v-else>Pay-as-you-go resources are available.</div>
                                            </template>
                                        </v-col>
                                        <v-col class="shrink">
                                            <v-btn v-if="isScalingEnabled" @click="enableScaling()" :loading="settingScaling" small color="primary" outlined>
                                                Enable
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-alert>

                                <template v-if="cloud === 'azure' && $isError(localNodePools)">
                                    <dashboard-error></dashboard-error>
                                </template>
                                <template v-else>
                                    <div class="d-flex align-center">
                                        <v-card flat color="grey lighten-3 mr-3">
                                            <v-card-text class="py-2">
                                                <v-radio-group v-model="cloudFamilySelected" row mandatory hide-details dense class="mt-0 pt-0">
                                                    <template v-slot:label>
                                                        <div class="mr-3 my-2">Optimized for</div>
                                                    </template>
                                                    <div class="d-flex flex-wrap mt-n2">
                                                        <v-card
                                                            class="mt-2 mr-2"
                                                            :elevation="cloudFamilySelected === 'cpu' ? 4 : 0"
                                                            :outlined="cloudFamilySelected !== 'cpu'">
                                                            <v-radio value="cpu" class="pa-2 pr-5 mr-0">
                                                                <template v-slot:label>
                                                                    <v-icon left small color="purple">mdi-memory</v-icon>
                                                                    CPU
                                                                </template>
                                                            </v-radio>
                                                        </v-card>
                                                        <v-card
                                                            class="mt-2 mr-2"
                                                            :elevation="cloudFamilySelected === 'gpu' ? 4 : 0"
                                                            :outlined="cloudFamilySelected !== 'gpu'">
                                                            <v-radio value="gpu" class="pa-2 pr-5 mr-0">
                                                                <template v-slot:label>
                                                                    <v-icon left small color="light-green">mdi-memory mdi-rotate-45</v-icon>
                                                                    GPU
                                                                </template>
                                                            </v-radio>
                                                        </v-card>
                                                        <v-card
                                                            class="mt-2"
                                                            :elevation="cloudFamilySelected === 'ssd' ? 4 : 0"
                                                            :outlined="cloudFamilySelected !== 'ssd'">
                                                            <v-radio value="ssd" class="pa-2 pr-5 mr-0">
                                                                <template v-slot:label>
                                                                    <v-icon left small color="orange">mdi-nas</v-icon>
                                                                    Storage
                                                                </template>
                                                            </v-radio>
                                                        </v-card>
                                                    </div>
                                                </v-radio-group>
                                            </v-card-text>
                                        </v-card>

                                        <v-spacer></v-spacer>

                                        <v-text-field
                                            prepend-inner-icon="mdi-magnify "
                                            autofocus
                                            hide-details
                                            solo
                                            flat
                                            dense
                                            placeholder="Search for a resource..."
                                            v-model="search.cloud"
                                            clearable
                                            class="mr-2"></v-text-field>
                                        <v-tooltip bottom>
                                            <template v-slot:activator="{ on }">
                                                <v-btn @click="refreshCloudItems" icon v-on="on" :loading="fetchingLocalNodePools">
                                                    <v-icon>refresh</v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Refresh Resource</span>
                                        </v-tooltip>
                                    </div>

                                    <v-row no-gutters class="py-4">
                                        <v-col cols="1">
                                            <v-btn :ripple="false" plain block class="justify-start text-notransform" @click="headerClicked('cpu', 'cloud')">
                                                <span class="subtitle-2 font-weight-medium text--secondary">vCPU</span>
                                                <v-icon small :class="headerClasses.cloud.cpu">mdi-arrow-up</v-icon>
                                            </v-btn>
                                        </v-col>
                                        <v-col cols="1">
                                            <v-btn :ripple="false" plain block class="justify-start text-notransform" @click="headerClicked('memory', 'cloud')">
                                                <span class="subtitle-2 font-weight-medium text--secondary">Memory</span>
                                                <v-icon small :class="headerClasses.cloud.memory">mdi-arrow-up</v-icon>
                                            </v-btn>
                                        </v-col>
                                        <v-col cols="2">
                                            <v-btn
                                                :ripple="false"
                                                plain
                                                block
                                                class="justify-start text-notransform"
                                                @click="headerClicked('gpu_type', 'cloud')">
                                                <span class="subtitle-2 font-weight-medium text--secondary">GPU Model</span>
                                                <v-icon small :class="headerClasses.cloud.gpu_type">mdi-arrow-up</v-icon>
                                            </v-btn>
                                        </v-col>
                                        <v-col cols="1">
                                            <v-btn :ripple="false" plain block class="justify-start text-notransform" @click="headerClicked('vram', 'cloud')">
                                                <span class="subtitle-2 font-weight-medium text--secondary">GPU Memory</span>
                                                <v-icon small :class="headerClasses.cloud.vram">mdi-arrow-up</v-icon>
                                            </v-btn>
                                        </v-col>
                                        <v-col cols="1">
                                            <v-btn :ripple="false" plain block class="justify-start text-notransform" @click="headerClicked('ssd', 'cloud')">
                                                <span class="subtitle-2 font-weight-medium text--secondary">Temp Storage</span>
                                                <v-icon small :class="headerClasses.cloud.ssd">mdi-arrow-up</v-icon>
                                            </v-btn>
                                        </v-col>
                                        <v-col cols="1">
                                            <v-btn
                                                :ripple="false"
                                                plain
                                                block
                                                class="justify-start text-notransform"
                                                @click="headerClicked('credits_per_hour', 'cloud')">
                                                <span class="subtitle-2 font-weight-medium text--secondary">Credit/hour</span>
                                                <v-icon small :class="headerClasses.cloud.credits_per_hour">mdi-arrow-up</v-icon>
                                            </v-btn>
                                        </v-col>
                                        <v-col cols="3" class="d-flex align-center px-4">
                                            <span class="subtitle-2 font-weight-medium text--secondary">Description</span>
                                        </v-col>
                                        <v-col cols="2" class="d-flex align-center justify-end">
                                            <span class="px-4 subtitle-2 font-weight-medium text--secondary">Actions</span>
                                        </v-col>
                                    </v-row>
                                    <v-data-iterator
                                        :items="filteredCloudItems"
                                        no-data-text="No resources found."
                                        :search="search.cloud"
                                        :sort-by="sortBy.cloud"
                                        :sort-desc="sortDesc.cloud"
                                        :options="tableOptions"
                                        :footer-props="{ 'items-per-page-options': itemsPerPageOptions }">
                                        <template v-slot:default="{ items }">
                                            <v-row>
                                                <v-col v-for="item in items" :key="item.node_pool" cols="12">
                                                    <v-card elevation="4" :class="getCloudItemClass(item)" :disabled="scalingApp">
                                                        <v-row no-gutters>
                                                            <v-col cols="1" class="d-flex align-center px-4">{{ item.cpu }}</v-col>
                                                            <v-col cols="1" class="d-flex align-center px-4">{{ toLocaleFixed(item.memory, 0) }} GB</v-col>
                                                            <v-col cols="2" class="d-flex align-center px-4">{{ item.gpu_type || '-' }}</v-col>
                                                            <v-col cols="1" class="d-flex align-center px-4">
                                                                <div v-if="item.vram">{{ toLocaleFixed(item.vram, 0) }} GB</div>
                                                                <div v-else>-</div>
                                                            </v-col>
                                                            <v-col cols="1" class="d-flex align-center px-4">
                                                                <div v-if="item.ssd">{{ toLocaleFixed(item.ssd, 0) }} GB</div>
                                                                <div v-else>-</div>
                                                            </v-col>
                                                            <v-col cols="1" class="d-flex align-center px-4">
                                                                <template v-if="item.credits_per_hour !== undefined && item.credits_per_hour !== null">
                                                                    {{ toLocaleFixed(item.credits_per_hour) }}
                                                                </template>
                                                                <template v-else>-</template>
                                                            </v-col>
                                                            <v-col cols="3" class="d-flex align-center px-4">
                                                                <div>{{ item.description || '-' }}</div>
                                                            </v-col>
                                                            <v-col cols="2" class="text-right pa-4">
                                                                <template
                                                                    v-if="
                                                                        item.node_pool === selectedApp.node_pool &&
                                                                        selectedApp.active_resource === 'node_pool' &&
                                                                        !scalingApp
                                                                    ">
                                                                    <v-btn color="success" small>
                                                                        <v-icon small>check</v-icon>
                                                                    </v-btn>
                                                                </template>
                                                                <template v-else>
                                                                    <v-btn
                                                                        color="primary"
                                                                        small
                                                                        :disabled="!canScale(item) || appScaled"
                                                                        @click="clickRow(item.node_pool, 'cloud')"
                                                                        :loading="item.node_pool === selectedRow.cloud && scalingApp">
                                                                        {{ selectionOnly ? 'select' : 'set' }}
                                                                    </v-btn>
                                                                </template>
                                                            </v-col>
                                                        </v-row>
                                                    </v-card>
                                                </v-col>
                                            </v-row>
                                        </template>
                                    </v-data-iterator>
                                </template>
                            </v-card-text>
                        </v-card>
                    </v-tab-item>
                </v-tabs-items>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import { enumsData } from '@/mixins/enums'
import { mapGetters, mapState } from 'vuex'
import { scaling } from '@/mixins/scaling'
import { resourceCloudTabs, resourceProviderLabel } from '@/utils/resourceSizes'
import { createBackendErrorHandler } from '@/utils/errorMessages'
import * as applicationErrors from '@/modules/application/errors'
import * as lifecycleErrors from '@/modules/snapshot/errors/lifecycle'
import * as permissionsErrors from '@/utils/errors/permissions'
import * as snapshotErrors from '@/modules/snapshot/errors/snapshots'
import * as spaceErrors from '@/modules/space/errors'

const { getBackendErrorMessage } = createBackendErrorHandler(applicationErrors, lifecycleErrors, permissionsErrors, snapshotErrors, spaceErrors)

const DashboardError = () => import('@/components/DashboardError.vue')

const activeClass = 'blue lighten-4 elevation-0'
const selectedClass = 'grey lighten-4 elevation-0'
const APP_LIST_REDIRECT_DELAY = 800

export default {
    name: 'AppScale',
    mixins: [enumsData, scaling],
    components: { DashboardError },
    props: {
        application: {
            type: Object,
            default: null
        },
        selectionOnly: {
            type: Boolean,
            default: false
        },
        hideHeader: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            selectedApp: null,
            tab: 0,
            selectedRow: {
                standard: null,
                custom: null,
                cloud: null
            },
            search: {
                standard: '',
                custom: '',
                cloud: ''
            },
            sortBy: {
                standard: 'cpu',
                custom: 'credits_per_hour',
                cloud: 'credits_per_hour'
            },
            sortDesc: {
                standard: undefined,
                custom: undefined,
                cloud: undefined
            },
            headerClasses: {
                standard: {
                    cpu: 'd-none',
                    memory: 'd-none'
                },
                custom: {
                    cpu: 'd-none',
                    memory: 'd-none',
                    gpu_type: 'd-none',
                    vram: 'd-none',
                    ssd: 'd-none',
                    credits_per_hour: 'd-none'
                },
                cloud: {
                    cpu: 'd-none',
                    memory: 'd-none',
                    gpu_type: 'd-none',
                    vram: 'd-none',
                    ssd: 'd-none',
                    credits_per_hour: 'd-none'
                }
            },
            nodeFamilySelected: 'cpu',
            cloudFamilySelected: 'cpu',
            fetchingConfig: false,
            itemsPerPageOptions: [25, 50, 100, -1],
            tableOptions: {
                page: 0,
                itemsPerPage: 25
            },
            resourcePoolBalance: null,
            precision: this.$appConfig?.VUE_APP_DASHBOARD_PRECISION,
            hpcCanBeEnabled: false,
            hpcDisabledReason: '',
            settingScaling: false,
            mappedResourcePoolId: null,
            scalingApp: false,
            appScaled: false
        }
    },
    created() {
        this.$store.dispatch('orgStore/fetchOrgSpaces', this.$route.params.oid)
        this.$store.dispatch('appStore/fetchLocalNodePools')
    },
    computed: {
        cloudTabs() {
            return resourceCloudTabs(this.allNodePools)
        },
        remainingCloudTabs() {
            return this.cloudTabs.filter(cloud => cloud !== 'azure')
        },
        NCUHint() {
            return this.isTrialSpace
                ? 'Cannot change Compute unit in trial. Please use credit-based sizes instead.'
                : 'Included resource sizes utilize the compute unit capacity of the plan. 1 Compute unit = 1 vCPU / 4 GB RAM maximum. Larger compute unit setting might result in slower application startup.'
        },
        filteredNCUOptions() {
            return this.NCUOptions.filter(option => {
                return option.cpu <= this.maxNCU
            })
        },
        maxNCU() {
            return this.currentSpaceType !== this.spaceTypes.EDUCATION_SPACE ? 16 : 4
        },
        allNodePools() {
            if (!Array.isArray(this.localNodePools)) return []
            return this.localNodePools.filter(item => {
                if (!item?.node_pool) return false
                return this.currentSpaceType !== this.spaceTypes.EDUCATION_SPACE || item.schedulable
            })
        },
        activeCloudName() {
            const firstCloudTab = 1
            const cloudIndex = this.tab - firstCloudTab
            if (cloudIndex < 0 || cloudIndex >= this.cloudTabs.length) return null
            return this.cloudTabs[cloudIndex]
        },
        customNodePools() {
            return this.allNodePools.filter(i => i.cloud !== 'switch')
        },
        cloudNodePools() {
            if (!this.activeCloudName) return []
            return this.allNodePools.filter(item => item.cloud === this.activeCloudName)
        },
        cpus() {
            return this.customNodePools.filter(i => i.cpu && !i.gpu_type && !i.ssd)
        },
        ssd() {
            return this.customNodePools.filter(i => i.ssd)
        },
        gpus() {
            return this.customNodePools.filter(i => i.gpu_type)
        },
        customItems() {
            switch (this.nodeFamilySelected) {
                case 'cpu':
                    return this.cpus
                case 'gpu':
                    return this.gpus
                case 'ssd':
                    return this.ssd
                default:
                    return []
            }
        },
        cloudCpuItems() {
            return this.cloudNodePools.filter(item => item.cpu && !item.gpu_type && !item.ssd)
        },
        cloudGpuItems() {
            return this.cloudNodePools.filter(item => item.gpu_type)
        },
        cloudSsdItems() {
            return this.cloudNodePools.filter(item => item.ssd)
        },
        filteredCloudItems() {
            switch (this.cloudFamilySelected) {
                case 'cpu':
                    return this.cloudCpuItems
                case 'gpu':
                    return this.cloudGpuItems
                case 'ssd':
                    return this.cloudSsdItems
                default:
                    return this.cloudNodePools
            }
        },
        fetchingAllData() {
            return this.fetchingLocalNodePools || (!this.application && this.applicationsFetching)
        },
        canScaleGlobal() {
            if (this.currentSpaceType === this.spaceTypes.EDUCATION_SPACE) {
                if (this.currentSpace.space_configs?.on_demand_gpu_for_students) {
                    return this.isSpaceAdmin && this.currentSpaceData?.beegfs_sync
                } else {
                    return this.isSpaceAdmin && this.isMasterInstance && this.currentSpaceData?.beegfs_sync
                }
            } else {
                return this.currentSpaceData?.beegfs_sync
            }
        },
        isScalingEnabled() {
            if (this.currentSpaceType === this.spaceTypes.EDUCATION_SPACE) {
                return this.isSpaceAdmin && this.hpcCanBeEnabled && this.isMasterInstance
            } else {
                return this.isSpaceAdmin && this.hpcCanBeEnabled
            }
        },
        ...mapState('spaceStore', ['mappedResources', 'currentSpace']),
        ...mapGetters('spaceStore', ['currentSpaceType', 'isTrialSpace', 'isSpaceAdmin', 'currentSpaceData']),
        ...mapState('snapshotStore', ['applications', 'applicationsFetching']),
        ...mapGetters('instanceStore', ['isMasterInstance'])
    },
    methods: {
        cloudTabLabel(cloud) {
            return cloud === 'azure' ? 'elastic' : resourceProviderLabel(cloud)
        },
        firstCloudTabIndex() {
            return this.cloudTabs.length ? 1 : null
        },
        headerClicked(column, type) {
            this.resetOtherColumns(column, type)
            if (this.sortBy[type] !== column) {
                this.sortDesc[type] = undefined
            }
            this.sortBy[type] = column
            switch (this.sortDesc[type]) {
                case undefined:
                    this.sortDesc[type] = false
                    this.headerClasses[type][column] = ['d-block', 'rotate-0']
                    break
                case false:
                    this.sortDesc[type] = true
                    this.headerClasses[type][column] = ['d-block', 'rotate-180']
                    break
                case true:
                    this.sortDesc[type] = undefined
                    this.headerClasses[type][column] = 'd-none'
                    break
            }
        },
        resetOtherColumns(currentColumn, type) {
            for (const prop in this.headerClasses[type]) {
                if (prop !== currentColumn) {
                    this.headerClasses[type][prop] = 'd-none'
                }
            }
        },
        getStandardItemClass(item) {
            if (item.id === this.selectedRow.standard) {
                return this.selectedApp.active_resource === 'ncu' ? activeClass : selectedClass
            }
        },
        getCustomItemClass(item) {
            if (item.node_pool === this.selectedRow.custom) {
                return this.selectedApp.active_resource === 'node_pool' ? activeClass : selectedClass
            }
        },
        getCloudItemClass(item) {
            if (item.node_pool === this.selectedRow.cloud) {
                return this.selectedApp.active_resource === 'node_pool' ? activeClass : selectedClass
            }
        },
        isCpuType(nodepool) {
            return this.cpus.some(item => item.node_pool === nodepool)
        },
        isSsdType(nodepool) {
            return this.ssd.some(item => item.node_pool === nodepool)
        },
        isGpuType(nodepool) {
            return this.gpus.some(item => item.node_pool === nodepool)
        },
        setSelectedNcu() {
            const ncuOption = this.NCUOptions.find(item => item.cpu === Number(this.selectedApp.ncu))
            this.selectedRow.standard = ncuOption.id
        },
        findNcuById(id) {
            return this.NCUOptions.find(item => item.id === id)
        },
        setSelectedNodepool() {
            const selectedNodePool = this.allNodePools.find(item => item.node_pool === this.selectedApp.node_pool)
            if (!selectedNodePool) return

            const nodepoolId = selectedNodePool.node_pool
            if (this.isCpuType(nodepoolId)) {
                this.nodeFamilySelected = 'cpu'
            }
            if (this.isSsdType(nodepoolId)) {
                this.nodeFamilySelected = 'ssd'
            }
            if (this.isGpuType(nodepoolId)) {
                this.nodeFamilySelected = 'gpu'
            }
            this.cloudFamilySelected = this.getNodepoolFamily(selectedNodePool)
            this.selectedRow.custom = this.selectedApp.node_pool
            this.selectedRow.cloud = this.selectedApp.node_pool
        },
        routeToAppList() {
            setTimeout(() => {
                this.$router.push({
                    name: 'snapshot-applications',
                    params: this.$route.params
                })
            }, APP_LIST_REDIRECT_DELAY)
        },
        async clickRow(id, type) {
            const previousId = this.selectedRow[type]
            this.selectedRow[type] = id
            const previousActiveResource = this.selectedApp.active_resource
            const previousNcu = this.selectedApp.ncu
            const previousNodePool = this.selectedApp.node_pool
            this.selectedApp.active_resource = type === 'standard' ? 'ncu' : 'node_pool'
            const query = type === 'standard' ? { ncu: this.findNcuById(id).cpu } : { node_pool: id }
            if (this.selectionOnly) {
                this.$emit('resource-selected', query)
                return
            }
            this.scalingApp = true
            try {
                await this.$axios.post(`applications/${this.$route.params.aid}/resource`, query)
                if (type === 'standard') {
                    this.selectedApp.ncu = query.ncu
                } else {
                    this.selectedApp.node_pool = query.node_pool
                }
                this.scalingApp = false
                this.appScaled = true
                this.routeToAppList()
            } catch (error) {
                this.scalingApp = false
                this.selectedApp.active_resource = previousActiveResource
                this.selectedApp.ncu = previousNcu
                this.selectedApp.node_pool = previousNodePool
                this.selectedRow[type] = previousId
                const snackBarText = getBackendErrorMessage(error, 'There was an error during the request')
                if (snackBarText) {
                    this.$store.dispatch('showSnackBar', { snackBarText, snackBarIcon: 'error' })
                }
            }
        },
        async fetchHPCStatus() {
            try {
                const { sid } = this.currentSpaceData
                const { data } = await this.$axios.get(`/spaces/${sid}/can_enable_hpc`)
                this.hpcCanBeEnabled = data.can_enable_hpc
                this.hpcDisabledReason = data.reason
            } catch (error) {
                const snackBarText = getBackendErrorMessage(
                    error,
                    'Failed to request HPC integration status, please reach out to support if the issue persists.'
                )
                if (snackBarText) {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText,
                        snackBarTimeout: 5000,
                        snackBarIcon: 'error'
                    })
                }
            }
        },
        async fetchResourceBalance(rpid) {
            try {
                const { data } = await this.$axios.get(`/resource/${rpid}/balance`)
                this.resourcePoolBalance = data.balance
            } catch (error) {
                this.resourcePoolBalance = error
            }
        },
        canScale(resource) {
            // Not enough credits
            if (resource.credits_per_hour / 6 > this.resourcePoolBalance) return false

            if (this.currentSpaceType === this.spaceTypes.EDUCATION_SPACE) {
                if (resource.gpu_type && this.currentSpace.space_configs?.on_demand_gpu_for_students) {
                    return this.isSpaceAdmin && this.currentSpaceData?.beegfs_sync && resource.schedulable
                } else {
                    return this.isSpaceAdmin && this.isMasterInstance && this.currentSpaceData?.beegfs_sync && resource.schedulable
                }
            } else {
                return this.currentSpaceData?.beegfs_sync
            }
        },
        enableScaling() {
            this.$axios
                .patch(`/spaces/${this.$route.params.sid}`, { beegfs_sync: true })
                .then(response => {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText: 'Pay-as-you-go resources turned on',
                        snackBarTimeout: 5000,
                        snackBarIcon: 'check_circle'
                    })
                    this.$store.dispatch('orgStore/fetchOrgSpaces', this.$route.params.oid)
                })
                .catch(error => {
                    const snackBarText = getBackendErrorMessage(
                        error,
                        'Failed to enable credit-based sizes, please reach out to support if the issue persists.'
                    )
                    if (snackBarText) {
                        this.$store.dispatch('showSnackBar', {
                            snackBarText,
                            snackBarTimeout: 5000,
                            snackBarIcon: 'error'
                        })
                    }
                })
                .finally(() => {
                    this.settingScaling = false
                })
        },
        refreshCloudItems() {
            return this.$store.dispatch('appStore/fetchLocalNodePools')
        }
    },
    watch: {
        cloudTabs: {
            handler() {
                const firstCloudTab = 1
                const nextCloudTab = this.firstCloudTabIndex()

                if (!nextCloudTab && this.tab >= firstCloudTab) {
                    this.tab = 0
                    return
                }

                if (this.tab < firstCloudTab) return

                if (this.activeCloudName) return

                if (nextCloudTab) {
                    this.tab = nextCloudTab
                }
            },
            immediate: true
        },
        fetchingAllData: {
            handler: function (fetching) {
                if (!fetching) {
                    this.selectedApp = this.application || this.applications.find(app => app.aid === Number(this.$route.params.aid))
                }
            },
            immediate: true
        },
        application: {
            handler(application) {
                if (application) this.selectedApp = application
            },
            immediate: true
        },
        selectedApp: {
            handler: function (resource) {
                if (!resource) return

                if (this.selectionOnly && !resource.active_resource) {
                    const defaultResource = this.filteredNCUOptions[0]
                    if (!defaultResource) return
                    this.tab = 0
                    this.selectedRow.standard = defaultResource.id
                    this.$emit('resource-selected', { ncu: defaultResource.cpu })
                    return
                }

                if (resource.active_resource === 'ncu') {
                    this.tab = 0
                } else {
                    this.tab = 1
                }
                this.setSelectedNcu()
                this.setSelectedNodepool()

                const selectedNodePool = this.allNodePools.find(item => item.node_pool === resource.node_pool)
                if (resource.active_resource === 'node_pool' && selectedNodePool?.cloud) {
                    const firstCloudTab = 1
                    const cloudTabIndex = this.cloudTabs.findIndex(cloud => cloud === selectedNodePool.cloud)
                    if (cloudTabIndex !== -1) {
                        this.tab = firstCloudTab + cloudTabIndex
                        this.cloudFamilySelected = this.getNodepoolFamily(selectedNodePool)
                    }
                }

                if (this.$route.query.tab === 'included' || this.$route.query.tab === 'nuvolos') {
                    this.tab = 0
                }

                if (this.$route.query.tab === 'credit') {
                    this.tab = this.firstCloudTabIndex() || 0
                }

                const requestedCloud = this.$route.query.cloud || (this.$route.query.tab === 'switch' ? 'switch' : null)
                if (this.cloudTabs.length > 0 && (this.$route.query.tab === 'cloud' || this.$route.query.tab === 'switch' || requestedCloud)) {
                    const firstCloudTab = 1
                    const cloudTabIndex = this.cloudTabs.findIndex(cloud => cloud === requestedCloud)
                    this.tab = cloudTabIndex === -1 ? firstCloudTab : firstCloudTab + cloudTabIndex
                    const requestedFamily = this.$route.query.cloudFamily || this.$route.query.switchFamily
                    if (['cpu', 'gpu', 'ssd'].includes(requestedFamily)) {
                        this.cloudFamilySelected = requestedFamily
                    }
                }
            },
            immediate: true
        },
        mappedResources: {
            handler(newVal) {
                if (newVal) {
                    this.mappedResourcePoolId = Number(Object.keys(newVal.HPC_INTER_COMPUTE)[0])
                    this.fetchResourceBalance(this.mappedResourcePoolId)
                }
            },
            immediate: true
        },
        currentSpaceData() {
            this.fetchHPCStatus()
        }
    }
}
</script>

<style scoped lang="scss">
::v-deep .v-breadcrumbs li {
    font-size: inherit;
}

::v-deep .v-card--link:before {
    border-radius: inherit;
}

.rotate-180 {
    transform: rotate(-180deg);
}
.rotate-0 {
    transform: rotate(0);
}
</style>
